import { useState, useEffect, useCallback, useRef } from 'react';
import { useAdminTheme } from './AdminThemeContext';
import axios from 'axios';
import { 
  Play, 
  TestTube2,
  CheckCircle,
  XCircle,
  AlertCircle,
  Loader2,
  Building2,
  DollarSign,
  Phone,
  Hash,
  Smartphone,
  RefreshCw,
  Trash2,
  ChevronRight,
  Clock,
  TrendingUp,
  ArrowRight,
  Copy,
  Check,
  Eye,
  FileText,
  Upload,
  Download,
  Layers,
  Plus,
  X
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PaymentSimulator = () => {
  const { isDarkMode } = useAdminTheme();
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [simulating, setSimulating] = useState(false);
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [selectedTest, setSelectedTest] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const [activeTab, setActiveTab] = useState('single'); // single, batch
  const [batchPayments, setBatchPayments] = useState([]);
  const [batchResult, setBatchResult] = useState(null);
  const [showBatchResultModal, setShowBatchResultModal] = useState(false);
  const [batchHistory, setBatchHistory] = useState([]);
  const fileInputRef = useRef(null);
  
  // Form state
  const [form, setForm] = useState({
    merchant_id: '',
    branch_id: '',
    amount: '500',
    currency: 'LRD',
    network: 'MTN',
    sender_number: '+231770123456',
    reference: '',
    payment_type: 'mobile_money',
    description: ''
  });

  const fetchMerchants = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/payment-simulator/merchants`, { withCredentials: true });
      setMerchants(response.data);
      if (response.data.length > 0 && !form.merchant_id) {
        setForm(prev => ({ ...prev, merchant_id: response.data[0].merchant_id }));
      }
    } catch (error) {
      console.error('Error fetching merchants:', error);
    }
  }, [form.merchant_id]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/payment-simulator/stats`, { withCredentials: true });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/payment-simulator/history?limit=20`, { withCredentials: true });
      setHistory(response.data);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBatchHistory = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/admin/payment-simulator/batches?limit=10`, { withCredentials: true });
      setBatchHistory(response.data);
    } catch (error) {
      console.error('Error fetching batch history:', error);
    }
  }, []);

  useEffect(() => {
    fetchMerchants();
    fetchStats();
    fetchHistory();
    fetchBatchHistory();
  }, [fetchMerchants, fetchStats, fetchHistory, fetchBatchHistory]);

  // Batch payment functions
  const addBatchPayment = () => {
    if (!form.merchant_id) {
      toast.error('Please select a merchant first');
      return;
    }
    
    const newPayment = {
      id: Date.now(),
      merchant_id: form.merchant_id,
      merchant_name: merchants.find(m => m.merchant_id === form.merchant_id)?.business_name || 'Unknown',
      branch_id: form.branch_id || null,
      amount: parseFloat(form.amount) || 500,
      currency: form.currency,
      network: form.network,
      sender_number: `+23177${Math.floor(1000000 + Math.random() * 9000000)}`,
      reference: null,
      description: `Batch payment #${batchPayments.length + 1}`
    };
    
    setBatchPayments(prev => [...prev, newPayment]);
    toast.success('Payment added to batch');
  };

  const removeBatchPayment = (id) => {
    setBatchPayments(prev => prev.filter(p => p.id !== id));
  };

  const clearBatchPayments = () => {
    setBatchPayments([]);
  };

  const handleBatchSimulate = async () => {
    if (batchPayments.length === 0) {
      toast.error('Add at least one payment to the batch');
      return;
    }

    setSimulating(true);
    try {
      const payload = {
        payments: batchPayments.map(p => ({
          merchant_id: p.merchant_id,
          branch_id: p.branch_id,
          amount: p.amount,
          currency: p.currency,
          network: p.network,
          sender_number: p.sender_number,
          reference: p.reference,
          description: p.description
        })),
        delay_ms: 50
      };
      
      const response = await axios.post(
        `${API_URL}/api/admin/payment-simulator/batch`,
        payload,
        { withCredentials: true }
      );
      
      setBatchResult(response.data);
      setShowBatchResultModal(true);
      
      if (response.data.successful > 0) {
        toast.success(`Batch completed: ${response.data.successful}/${response.data.total} successful`);
        setBatchPayments([]);
      }
      
      fetchStats();
      fetchHistory();
      fetchBatchHistory();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Batch simulation failed');
    } finally {
      setSimulating(false);
    }
  };

  const handleCSVUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setSimulating(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API_URL}/api/admin/payment-simulator/batch/csv`,
        formData,
        { 
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );
      
      setBatchResult(response.data);
      setShowBatchResultModal(true);
      toast.success(`CSV batch completed: ${response.data.successful}/${response.data.total} successful`);
      
      fetchStats();
      fetchHistory();
      fetchBatchHistory();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'CSV upload failed');
    } finally {
      setSimulating(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const downloadCSVTemplate = () => {
    const template = 'merchant_id,amount,currency,network,sender_number,reference,description\nmerch_example123,500,LRD,MTN,+231770123456,,Test payment 1\nmerch_example123,1000,USD,Orange,+231880987654,,Test payment 2';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'payment_batch_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleSimulate = async (e) => {
    e.preventDefault();
    
    if (!form.merchant_id) {
      toast.error('Please select a merchant');
      return;
    }
    
    if (!form.amount || parseFloat(form.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }

    setSimulating(true);
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount),
        branch_id: form.branch_id || null,
        reference: form.reference || null
      };
      
      const response = await axios.post(
        `${API_URL}/api/admin/payment-simulator/simulate`,
        payload,
        { withCredentials: true }
      );
      
      setLastResult(response.data);
      setShowResultModal(true);
      
      if (response.data.success) {
        toast.success('Payment simulated successfully!');
        // Clear reference for next test
        setForm(prev => ({ ...prev, reference: '' }));
      } else {
        toast.error(response.data.message || 'Simulation failed');
      }
      
      // Refresh stats and history
      fetchStats();
      fetchHistory();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Simulation failed');
      setLastResult({
        success: false,
        message: error.response?.data?.detail || 'Simulation failed',
        error: error.message
      });
      setShowResultModal(true);
    } finally {
      setSimulating(false);
    }
  };

  const handleClearTestData = async () => {
    if (!window.confirm('Are you sure you want to clear all test payment data? This cannot be undone.')) {
      return;
    }
    
    try {
      const response = await axios.delete(`${API_URL}/api/admin/payment-simulator/clear`, { withCredentials: true });
      toast.success(`Cleared ${response.data.deleted.test_payments} test records`);
      fetchStats();
      fetchHistory();
    } catch (error) {
      toast.error('Failed to clear test data');
    }
  };

  const copyToClipboard = (text, field) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const generateRandomRef = () => {
    const ref = `FL-${new Date().toISOString().slice(0,10).replace(/-/g,'')}-${Math.random().toString(36).substr(2, 6).toUpperCase()}`;
    setForm(prev => ({ ...prev, reference: ref }));
  };

  const generateRandomPhone = () => {
    const phone = `+23177${Math.floor(1000000 + Math.random() * 9000000)}`;
    setForm(prev => ({ ...prev, sender_number: phone }));
  };

  const selectedMerchant = merchants.find(m => m.merchant_id === form.merchant_id);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-teal-500" />
      </div>
    );
  }

  return (
    <div className={`min-h-screen p-6 ${isDarkMode ? 'bg-slate-900' : 'bg-gray-50'}`} data-testid="payment-simulator-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className={`text-2xl font-semibold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
            <TestTube2 className="w-7 h-7 text-teal-500" />
            Payment Simulator
          </h1>
          <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
            Test payment flows before connecting live infrastructure
          </p>
        </div>
        <button
          onClick={handleClearTestData}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            isDarkMode 
              ? 'bg-red-900/30 text-red-400 hover:bg-red-900/50' 
              : 'bg-red-50 text-red-600 hover:bg-red-100'
          }`}
          data-testid="clear-test-data-btn"
        >
          <Trash2 className="w-4 h-4" />
          Clear Test Data
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-4 gap-4 mb-6">
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-white shadow-sm'}`}>
            <div className={`text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Tests Today
            </div>
            <div className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
              {stats.today.total}
            </div>
          </div>
          
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-white shadow-sm'}`}>
            <div className={`text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Successful
            </div>
            <div className="text-2xl font-bold text-green-500">
              {stats.today.successful}
            </div>
          </div>
          
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-white shadow-sm'}`}>
            <div className={`text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Failed
            </div>
            <div className="text-2xl font-bold text-red-500">
              {stats.today.failed}
            </div>
          </div>
          
          <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-white shadow-sm'}`}>
            <div className={`text-sm font-medium mb-1 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
              Success Rate
            </div>
            <div className={`text-2xl font-bold ${stats.today.success_rate >= 80 ? 'text-green-500' : stats.today.success_rate >= 50 ? 'text-yellow-500' : 'text-red-500'}`}>
              {stats.today.success_rate}%
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-6">
        {/* Simulator Form */}
        <div className={`col-span-2 p-6 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-white shadow-sm'}`}>
          {/* Tabs */}
          <div className={`flex gap-1 p-1 rounded-lg mb-4 w-fit ${isDarkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
            <button
              onClick={() => setActiveTab('single')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                activeTab === 'single'
                  ? 'bg-teal-500 text-white shadow'
                  : isDarkMode ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-800'
              }`}
              data-testid="tab-single"
            >
              <Play className="w-4 h-4" />
              Single Payment
            </button>
            <button
              onClick={() => setActiveTab('batch')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-2 ${
                activeTab === 'batch'
                  ? 'bg-teal-500 text-white shadow'
                  : isDarkMode ? 'text-slate-300 hover:text-white' : 'text-slate-600 hover:text-slate-800'
              }`}
              data-testid="tab-batch"
            >
              <Layers className="w-4 h-4" />
              Batch Payments
            </button>
          </div>

          {activeTab === 'single' ? (
            /* Single Payment Form */
            <>
              <h2 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                <Play className="w-5 h-5 text-teal-500" />
                Simulate Single Payment
              </h2>
          
              <form onSubmit={handleSimulate} className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {/* Merchant */}
                  <div className="col-span-2">
                    <label className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                      Merchant *
                    </label>
                    <select
                      value={form.merchant_id}
                      onChange={(e) => setForm({ ...form, merchant_id: e.target.value, branch_id: '' })}
                      className={`w-full px-4 py-2.5 rounded-lg border ${
                        isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200'
                      }`}
                      required
                      data-testid="merchant-select"
                    >
                      <option value="">Select merchant</option>
                      {merchants.map(m => (
                        <option key={m.merchant_id} value={m.merchant_id}>
                          {m.business_name || m.name} ({m.email})
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Branch */}
                  {selectedMerchant?.branches?.length > 0 && (
                    <div className="col-span-2">
                      <label className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                        Branch (Optional)
                      </label>
                      <select
                        value={form.branch_id}
                        onChange={(e) => setForm({ ...form, branch_id: e.target.value })}
                        className={`w-full px-4 py-2.5 rounded-lg border ${
                          isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200'
                        }`}
                        data-testid="branch-select"
                      >
                        <option value="">All Branches</option>
                        {selectedMerchant.branches.map(b => (
                          <option key={b.branch_id} value={b.branch_id}>
                            {b.branch_name} ({b.branch_code})
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

              {/* Amount */}
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Amount *
                </label>
                <div className="relative">
                  <DollarSign className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-400'}`} />
                  <input
                    type="number"
                    step="0.01"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    className={`w-full pl-10 pr-4 py-2.5 rounded-lg border ${
                      isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200'
                    }`}
                    required
                    data-testid="amount-input"
                  />
                </div>
              </div>

              {/* Currency */}
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Currency
                </label>
                <select
                  value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}
                  className={`w-full px-4 py-2.5 rounded-lg border ${
                    isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200'
                  }`}
                  data-testid="currency-select"
                >
                  <option value="LRD">LRD (Liberian Dollar)</option>
                  <option value="USD">USD (US Dollar)</option>
                </select>
              </div>

              {/* Network */}
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Network *
                </label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, network: 'MTN' })}
                    className={`flex-1 py-2.5 px-4 rounded-lg font-medium transition-all ${
                      form.network === 'MTN'
                        ? 'bg-yellow-500 text-black'
                        : isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-600'
                    }`}
                    data-testid="network-mtn"
                  >
                    MTN
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, network: 'Orange' })}
                    className={`flex-1 py-2.5 px-4 rounded-lg font-medium transition-all ${
                      form.network === 'Orange'
                        ? 'bg-orange-500 text-white'
                        : isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-600'
                    }`}
                    data-testid="network-orange"
                  >
                    Orange
                  </button>
                </div>
              </div>

              {/* Payment Type */}
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Payment Type
                </label>
                <select
                  value={form.payment_type}
                  onChange={(e) => setForm({ ...form, payment_type: e.target.value })}
                  className={`w-full px-4 py-2.5 rounded-lg border ${
                    isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200'
                  }`}
                  data-testid="payment-type-select"
                >
                  <option value="mobile_money">Mobile Money</option>
                  <option value="qr">QR Payment</option>
                  <option value="direct">Direct Transfer</option>
                </select>
              </div>

              {/* Sender Number */}
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Sender Number *
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Phone className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-400'}`} />
                    <input
                      type="text"
                      value={form.sender_number}
                      onChange={(e) => setForm({ ...form, sender_number: e.target.value })}
                      className={`w-full pl-10 pr-4 py-2.5 rounded-lg border ${
                        isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200'
                      }`}
                      placeholder="+231770123456"
                      required
                      data-testid="sender-input"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={generateRandomPhone}
                    className={`px-3 py-2.5 rounded-lg ${isDarkMode ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
                    title="Generate random"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Reference */}
              <div>
                <label className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Reference (Auto-generated if empty)
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Hash className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-slate-400' : 'text-slate-400'}`} />
                    <input
                      type="text"
                      value={form.reference}
                      onChange={(e) => setForm({ ...form, reference: e.target.value })}
                      className={`w-full pl-10 pr-4 py-2.5 rounded-lg border ${
                        isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200'
                      }`}
                      placeholder="FL-20260314-ABC123"
                      data-testid="reference-input"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={generateRandomRef}
                    className={`px-3 py-2.5 rounded-lg ${isDarkMode ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'}`}
                    title="Generate random"
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Description */}
              <div className="col-span-2">
                <label className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Description (Optional)
                </label>
                <input
                  type="text"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className={`w-full px-4 py-2.5 rounded-lg border ${
                    isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200'
                  }`}
                  placeholder="Test payment for order #123"
                  data-testid="description-input"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={simulating}
              className="w-full py-3 bg-teal-500 hover:bg-teal-600 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
              data-testid="simulate-btn"
            >
              {simulating ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Simulating...
                </>
              ) : (
                <>
                  <Play className="w-5 h-5" />
                  Simulate Payment
                </>
              )}
            </button>
          </form>
            </>
          ) : (
            /* Batch Payment Section */
            <>
              <div className="flex items-center justify-between mb-4">
                <h2 className={`text-lg font-semibold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                  <Layers className="w-5 h-5 text-teal-500" />
                  Batch Payment Simulator
                </h2>
                <div className="flex gap-2">
                  <button
                    onClick={downloadCSVTemplate}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm ${
                      isDarkMode ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' : 'bg-gray-100 hover:bg-gray-200'
                    }`}
                  >
                    <Download className="w-4 h-4" />
                    CSV Template
                  </button>
                  <label className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm cursor-pointer ${
                    isDarkMode ? 'bg-slate-700 hover:bg-slate-600 text-slate-300' : 'bg-gray-100 hover:bg-gray-200'
                  }`}>
                    <Upload className="w-4 h-4" />
                    Upload CSV
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".csv"
                      onChange={handleCSVUpload}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              {/* Merchant Selector for Manual Add */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div>
                  <label className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Merchant
                  </label>
                  <select
                    value={form.merchant_id}
                    onChange={(e) => setForm({ ...form, merchant_id: e.target.value })}
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${
                      isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200'
                    }`}
                  >
                    <option value="">Select merchant</option>
                    {merchants.map(m => (
                      <option key={m.merchant_id} value={m.merchant_id}>
                        {m.business_name || m.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Amount ({form.currency})
                  </label>
                  <input
                    type="number"
                    value={form.amount}
                    onChange={(e) => setForm({ ...form, amount: e.target.value })}
                    className={`w-full px-3 py-2 rounded-lg border text-sm ${
                      isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-gray-200'
                    }`}
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium mb-1.5 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                    Network
                  </label>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, network: 'MTN' })}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium ${
                        form.network === 'MTN'
                          ? 'bg-yellow-500 text-black'
                          : isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-100'
                      }`}
                    >
                      MTN
                    </button>
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, network: 'Orange' })}
                      className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium ${
                        form.network === 'Orange'
                          ? 'bg-orange-500 text-white'
                          : isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-100'
                      }`}
                    >
                      Orange
                    </button>
                  </div>
                </div>
              </div>

              <button
                onClick={addBatchPayment}
                className={`w-full py-2 mb-4 rounded-lg border-2 border-dashed flex items-center justify-center gap-2 text-sm font-medium transition-colors ${
                  isDarkMode 
                    ? 'border-slate-600 text-slate-400 hover:border-teal-500 hover:text-teal-400' 
                    : 'border-gray-300 text-gray-500 hover:border-teal-500 hover:text-teal-500'
                }`}
              >
                <Plus className="w-4 h-4" />
                Add Payment to Batch
              </button>

              {/* Batch Payments List */}
              <div className={`rounded-lg border mb-4 ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
                <div className={`px-4 py-2 border-b flex items-center justify-between ${
                  isDarkMode ? 'bg-slate-700/50 border-slate-700' : 'bg-gray-50 border-gray-200'
                }`}>
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                    Batch Queue ({batchPayments.length} payments)
                  </span>
                  {batchPayments.length > 0 && (
                    <button
                      onClick={clearBatchPayments}
                      className="text-xs text-red-500 hover:text-red-400"
                    >
                      Clear All
                    </button>
                  )}
                </div>
                <div className="max-h-48 overflow-auto">
                  {batchPayments.length === 0 ? (
                    <p className={`text-center py-6 text-sm ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                      No payments in batch. Add payments above or upload a CSV.
                    </p>
                  ) : (
                    batchPayments.map((payment, idx) => (
                      <div 
                        key={payment.id}
                        className={`px-4 py-2 flex items-center justify-between border-b last:border-0 ${
                          isDarkMode ? 'border-slate-700' : 'border-gray-100'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className={`text-xs font-mono px-2 py-0.5 rounded ${
                            isDarkMode ? 'bg-slate-700 text-slate-400' : 'bg-gray-100 text-gray-500'
                          }`}>
                            #{idx + 1}
                          </span>
                          <div>
                            <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                              {payment.currency} {payment.amount.toFixed(2)}
                            </span>
                            <span className={`text-xs ml-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                              • {payment.merchant_name} • {payment.network}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => removeBatchPayment(payment.id)}
                          className="p-1 rounded hover:bg-red-100 text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Batch Total */}
              {batchPayments.length > 0 && (
                <div className={`p-3 rounded-lg mb-4 ${isDarkMode ? 'bg-teal-900/20' : 'bg-teal-50'}`}>
                  <div className="flex items-center justify-between">
                    <span className={`text-sm ${isDarkMode ? 'text-teal-300' : 'text-teal-700'}`}>
                      Total Amount
                    </span>
                    <span className={`font-semibold ${isDarkMode ? 'text-teal-300' : 'text-teal-700'}`}>
                      {batchPayments[0]?.currency || 'LRD'} {batchPayments.reduce((sum, p) => sum + p.amount, 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              )}

              <button
                onClick={handleBatchSimulate}
                disabled={simulating || batchPayments.length === 0}
                className="w-full py-3 bg-teal-500 hover:bg-teal-600 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                data-testid="batch-simulate-btn"
              >
                {simulating ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Processing Batch...
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5" />
                    Run Batch ({batchPayments.length} payments)
                  </>
                )}
              </button>
            </>
          )}
        </div>

        {/* Recent Tests */}
        <div className={`p-6 rounded-xl ${isDarkMode ? 'bg-slate-800' : 'bg-white shadow-sm'}`}>
          <h2 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
            <Clock className="w-5 h-5 text-teal-500" />
            Recent Tests
          </h2>
          
          <div className="space-y-3 max-h-[500px] overflow-auto">
            {history.length === 0 ? (
              <p className={`text-sm text-center py-8 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                No test payments yet
              </p>
            ) : (
              history.map((test) => (
                <div
                  key={test.test_id}
                  className={`p-3 rounded-lg cursor-pointer transition-colors ${
                    isDarkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                  onClick={() => {
                    setSelectedTest(test);
                    setLastResult(test);
                    setShowResultModal(true);
                  }}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className={`font-medium text-sm ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                      {test.currency} {test.amount?.toFixed(2)}
                    </span>
                    {test.success ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <XCircle className="w-4 h-4 text-red-500" />
                    )}
                  </div>
                  <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                    {test.merchant_name} • {test.network}
                  </div>
                  <div className={`text-xs mt-1 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
                    {new Date(test.simulated_at).toLocaleString()}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Result Modal */}
      {showResultModal && lastResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`w-full max-w-2xl max-h-[80vh] overflow-auto rounded-xl p-6 ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-xl font-semibold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                {lastResult.success ? (
                  <CheckCircle className="w-6 h-6 text-green-500" />
                ) : (
                  <XCircle className="w-6 h-6 text-red-500" />
                )}
                {lastResult.success ? 'Simulation Successful' : 'Simulation Failed'}
              </h3>
              <button
                onClick={() => setShowResultModal(false)}
                className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}
              >
                ×
              </button>
            </div>

            <div className={`p-4 rounded-lg mb-4 ${
              lastResult.success 
                ? isDarkMode ? 'bg-green-900/20 border border-green-800' : 'bg-green-50 border border-green-200'
                : isDarkMode ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'
            }`}>
              <p className={lastResult.success ? 'text-green-500' : 'text-red-500'}>
                {lastResult.message}
              </p>
            </div>

            {/* Simulated SMS */}
            {lastResult.simulated_sms && (
              <div className="mb-4">
                <h4 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Simulated SMS Message
                </h4>
                <div className={`relative p-3 rounded-lg font-mono text-sm ${
                  isDarkMode ? 'bg-slate-700 text-slate-300' : 'bg-gray-100 text-gray-800'
                }`}>
                  {lastResult.simulated_sms}
                  <button
                    onClick={() => copyToClipboard(lastResult.simulated_sms, 'sms')}
                    className="absolute top-2 right-2 p-1 rounded hover:bg-slate-600"
                  >
                    {copiedField === 'sms' ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              </div>
            )}

            {/* IDs */}
            <div className="grid grid-cols-2 gap-4 mb-4">
              {lastResult.test_id && (
                <div>
                  <span className={`text-xs font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Test ID</span>
                  <p className={`font-mono text-sm ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{lastResult.test_id}</p>
                </div>
              )}
              {lastResult.transaction_id && (
                <div>
                  <span className={`text-xs font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Transaction ID</span>
                  <p className={`font-mono text-sm ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{lastResult.transaction_id}</p>
                </div>
              )}
              {lastResult.sms_id && (
                <div>
                  <span className={`text-xs font-medium ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>SMS ID</span>
                  <p className={`font-mono text-sm ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>{lastResult.sms_id}</p>
                </div>
              )}
            </div>

            {/* Processing Steps */}
            {lastResult.steps && lastResult.steps.length > 0 && (
              <div>
                <h4 className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-700'}`}>
                  Processing Steps
                </h4>
                <div className="space-y-2">
                  {lastResult.steps.map((step, idx) => (
                    <div
                      key={idx}
                      className={`flex items-center gap-3 p-2 rounded-lg ${
                        isDarkMode ? 'bg-slate-700' : 'bg-gray-50'
                      }`}
                    >
                      {step.status === 'completed' ? (
                        <CheckCircle className="w-4 h-4 text-green-500" />
                      ) : step.status === 'failed' ? (
                        <XCircle className="w-4 h-4 text-red-500" />
                      ) : (
                        <Loader2 className="w-4 h-4 text-yellow-500 animate-spin" />
                      )}
                      <div className="flex-1">
                        <span className={`text-sm font-medium capitalize ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                          {step.step.replace(/_/g, ' ')}
                        </span>
                        {step.error && (
                          <p className="text-xs text-red-500">{step.error}</p>
                        )}
                        {step.transaction_id && (
                          <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            TX: {step.transaction_id}
                          </p>
                        )}
                        {step.action && (
                          <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                            Action: {step.action}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Verification Status */}
            <div className="grid grid-cols-3 gap-4 mt-4 pt-4 border-t border-slate-600">
              <div className={`p-3 rounded-lg text-center ${
                lastResult.merchant_matched 
                  ? isDarkMode ? 'bg-green-900/20' : 'bg-green-50'
                  : isDarkMode ? 'bg-slate-700' : 'bg-gray-100'
              }`}>
                {lastResult.merchant_matched ? (
                  <CheckCircle className="w-5 h-5 text-green-500 mx-auto mb-1" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500 mx-auto mb-1" />
                )}
                <span className={`text-xs ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Merchant Match</span>
              </div>
              <div className={`p-3 rounded-lg text-center ${
                lastResult.parsing_success 
                  ? isDarkMode ? 'bg-green-900/20' : 'bg-green-50'
                  : isDarkMode ? 'bg-slate-700' : 'bg-gray-100'
              }`}>
                {lastResult.parsing_success ? (
                  <CheckCircle className="w-5 h-5 text-green-500 mx-auto mb-1" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500 mx-auto mb-1" />
                )}
                <span className={`text-xs ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>SMS Parsed</span>
              </div>
              <div className={`p-3 rounded-lg text-center ${
                lastResult.reference_matched 
                  ? isDarkMode ? 'bg-green-900/20' : 'bg-green-50'
                  : isDarkMode ? 'bg-slate-700' : 'bg-gray-100'
              }`}>
                {lastResult.reference_matched ? (
                  <CheckCircle className="w-5 h-5 text-green-500 mx-auto mb-1" />
                ) : (
                  <AlertCircle className="w-5 h-5 text-yellow-500 mx-auto mb-1" />
                )}
                <span className={`text-xs ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Ref Match</span>
              </div>
            </div>

            <button
              onClick={() => setShowResultModal(false)}
              className={`w-full mt-4 py-2.5 rounded-lg font-medium ${
                isDarkMode ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
              }`}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Batch Result Modal */}
      {showBatchResultModal && batchResult && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`w-full max-w-2xl max-h-[80vh] overflow-auto rounded-xl p-6 ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`text-xl font-semibold flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                <Layers className="w-6 h-6 text-teal-500" />
                Batch Simulation Results
              </h3>
              <button
                onClick={() => setShowBatchResultModal(false)}
                className={`p-2 rounded-lg ${isDarkMode ? 'hover:bg-slate-700' : 'hover:bg-gray-100'}`}
              >
                ×
              </button>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-4 gap-4 mb-4">
              <div className={`p-3 rounded-lg text-center ${isDarkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                <div className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                  {batchResult.total}
                </div>
                <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Total</div>
              </div>
              <div className={`p-3 rounded-lg text-center ${isDarkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
                <div className="text-2xl font-bold text-green-500">
                  {batchResult.successful}
                </div>
                <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Successful</div>
              </div>
              <div className={`p-3 rounded-lg text-center ${isDarkMode ? 'bg-red-900/30' : 'bg-red-50'}`}>
                <div className="text-2xl font-bold text-red-500">
                  {batchResult.failed}
                </div>
                <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Failed</div>
              </div>
              <div className={`p-3 rounded-lg text-center ${isDarkMode ? 'bg-teal-900/30' : 'bg-teal-50'}`}>
                <div className={`text-2xl font-bold ${isDarkMode ? 'text-teal-400' : 'text-teal-600'}`}>
                  {batchResult.success_rate}%
                </div>
                <div className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>Success Rate</div>
              </div>
            </div>

            {/* Results List */}
            <div className={`rounded-lg border max-h-64 overflow-auto ${isDarkMode ? 'border-slate-700' : 'border-gray-200'}`}>
              {batchResult.results?.map((result, idx) => (
                <div 
                  key={idx}
                  className={`px-4 py-3 flex items-center justify-between border-b last:border-0 ${
                    isDarkMode ? 'border-slate-700' : 'border-gray-100'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    {result.success ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500" />
                    )}
                    <div>
                      <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                        {result.currency} {result.amount?.toFixed(2)}
                      </span>
                      {result.merchant_name && (
                        <span className={`text-sm ml-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          • {result.merchant_name}
                        </span>
                      )}
                    </div>
                  </div>
                  {result.success ? (
                    <span className={`text-xs font-mono ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                      {result.transaction_id}
                    </span>
                  ) : (
                    <span className="text-xs text-red-500">
                      {result.error}
                    </span>
                  )}
                </div>
              ))}
            </div>

            <button
              onClick={() => setShowBatchResultModal(false)}
              className={`w-full mt-4 py-2.5 rounded-lg font-medium ${
                isDarkMode ? 'bg-slate-700 hover:bg-slate-600 text-white' : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
              }`}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentSimulator;
